import React, { useState, useEffect } from 'react';
import {
  Calculator, Sparkles, Users, Plus, Trash2, Loader2, Clock, BookOpen, AlertCircle, SlidersHorizontal, ClipboardList, ExternalLink, FlaskConical, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { MaturitySliderWithGuide } from '../MaturitySliderWithGuide';
import { MaturityAssessmentOverlay } from './MaturityAssessmentOverlay';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/api';

const PROFILE_PRESETS = {
  conservative: {
    name: 'Conservative', description: 'Risk-averse organization with careful, measured approach to change',
    icon: '🛡️', color: 'blue',
    settings: { preferred_elasticity_scenario: 'conservative', current_maturity: 35, dampening_factors: { adoption_speed: 0.55, leadership_alignment: 0.65, operational_readiness: 0.60 } }
  },
  moderate: {
    name: 'Balanced Growth', description: 'Steady, sustainable growth with balanced risk tolerance',
    icon: '⚖️', color: 'teal',
    settings: { preferred_elasticity_scenario: 'moderate', current_maturity: 50, dampening_factors: { adoption_speed: 0.70, leadership_alignment: 0.80, operational_readiness: 0.75 } }
  },
  aggressive: {
    name: 'Aggressive Growth', description: 'Fast-moving organization pushing for rapid transformation',
    icon: '🚀', color: 'purple',
    settings: { preferred_elasticity_scenario: 'aggressive', current_maturity: 65, dampening_factors: { adoption_speed: 0.85, leadership_alignment: 0.90, operational_readiness: 0.85 } }
  },
  startup: {
    name: 'Startup / Scale-up', description: 'High agility, limited maturity, strong leadership buy-in',
    icon: '💡', color: 'amber',
    settings: { preferred_elasticity_scenario: 'aggressive', current_maturity: 25, dampening_factors: { adoption_speed: 0.90, leadership_alignment: 0.95, operational_readiness: 0.55 } }
  },
  enterprise: {
    name: 'Mature Enterprise', description: 'Established processes, high maturity, measured change adoption',
    icon: '🏢', color: 'slate',
    settings: { preferred_elasticity_scenario: 'moderate', current_maturity: 80, dampening_factors: { adoption_speed: 0.60, leadership_alignment: 0.75, operational_readiness: 0.90 } }
  },
  transformation: {
    name: 'Digital Transformation', description: 'Organization undergoing major change initiative',
    icon: '⚡', color: 'orange',
    settings: { preferred_elasticity_scenario: 'aggressive', current_maturity: 45, dampening_factors: { adoption_speed: 0.75, leadership_alignment: 0.95, operational_readiness: 0.65 } }
  }
};

function MaturityMethodToggle({ profile, setProfile, orgId }) {
  const [showOverlay, setShowOverlay] = useState(false);
  // Latest Maturity Assessment for the active org. When present
  // and its overall_score doesn't match the current profile value,
  // we show a "Sync from assessment" prompt and (on first load
  // after a new assessment) auto-adopt the score so the slider
  // stays evidence-grounded.
  const [latestAssessment, setLatestAssessment] = useState(null);
  const [autoAdopted, setAutoAdopted] = useState(false);

  // Re-fetch when org context changes so org-switchers see the
  // correct assessment-driven default per organization.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = orgId ? `?organization_id=${orgId}` : '';
        const res = await api.get(`/maturity-assessment/latest${params}`);
        if (cancelled) return;
        const a = res?.data?.assessment;
        if (!a || a.overall_score == null) {
          setLatestAssessment(null);
          return;
        }
        setLatestAssessment(a);
        // Auto-adopt the assessment score on first load when the
        // slider doesn't already match it. This stops admins from
        // having to "remember to update the slider after taking
        // the assessment". The user can still drag it after.
        if (!autoAdopted) {
          const assessedRounded = Math.round(a.overall_score);
          if (Math.round(profile.current_maturity || 0) !== assessedRounded) {
            setProfile({ ...profile, current_maturity: assessedRounded });
          }
          setAutoAdopted(true);
        }
      } catch {
        if (!cancelled) setLatestAssessment(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const profileVal = Math.round(profile.current_maturity || 0);
  const assessedVal = latestAssessment ? Math.round(latestAssessment.overall_score) : null;
  const isInSync = assessedVal !== null && profileVal === assessedVal;
  const isOverridden = assessedVal !== null && profileVal !== assessedVal;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-slate-800">Organizational Maturity</h4>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowOverlay(true)}
          className="text-teal-600 border-teal-200 hover:bg-teal-50"
          data-testid="launch-assessment-btn"
        >
          <ClipboardList className="h-3.5 w-3.5 mr-1.5" /> Take Detailed Assessment
        </Button>
      </div>

      {/* Source-of-truth indicator. Mirrors the Single Measure
          page so admins always know whether the slider value is
          grounded in the formal assessment or is a manual judgment
          override. One-click revert when overridden. */}
      {latestAssessment && (
        <div
          className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600"
          data-testid="org-maturity-source-indicator"
        >
          {isInSync ? (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">
                <CheckCircle className="h-3 w-3" /> Synced from Maturity Assessment
              </span>
              <span className="text-slate-500">
                {latestAssessment.maturity_band ? `${latestAssessment.maturity_band} · ` : ''}
                Last assessed {latestAssessment.created_at ? new Date(latestAssessment.created_at).toLocaleDateString() : 'recently'}
              </span>
            </>
          ) : (
            <>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold uppercase tracking-wide">
                Manual override
              </span>
              <span>
                Latest assessment scored <strong>{assessedVal}%</strong>
                {latestAssessment.maturity_band ? ` (${latestAssessment.maturity_band})` : ''}
                {' · '}
                <button
                  type="button"
                  className="text-teal-600 hover:underline font-semibold"
                  onClick={() => setProfile({ ...profile, current_maturity: assessedVal })}
                  data-testid="org-maturity-revert-btn"
                >
                  Use assessment value
                </button>
              </span>
            </>
          )}
        </div>
      )}

      <MaturitySliderWithGuide
        value={profile.current_maturity || 50}
        onChange={(v) => setProfile({ ...profile, current_maturity: v })}
        showTitle={false}
        testId="maturity-profile-slider"
      />

      <p className="text-xs text-slate-400">Use the slider for a quick estimate, or take the <button onClick={() => setShowOverlay(true)} className="text-teal-600 underline hover:text-teal-800">30-question diagnostic</button> for a more accurate, auditable score.</p>

      {showOverlay && (
        <MaturityAssessmentOverlay
          onClose={() => setShowOverlay(false)}
          onComplete={(result) => {
            // After submitting a fresh assessment, mirror it to the
            // slider AND refresh our local "latest" cache so the
            // synced indicator reflects the new score immediately.
            const rounded = Math.round(result.overall_score);
            setProfile({ ...profile, current_maturity: rounded });
            setLatestAssessment({
              overall_score: result.overall_score,
              maturity_band: result.maturity_band,
              created_at: new Date().toISOString(),
            });
          }}
          preselectedOrgId={orgId}
        />
      )}
    </div>
  );
}

export function OrgDefaultsTab({
  profile,
  setProfile,
  orgId,
  customPresets,
  loadingPresets,
  setSavePresetDialogOpen,
  applyCustomPreset,
  deleteConfirmId,
  setDeleteConfirmId,
  handleDeletePreset,
  getPresetColorClass
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-5 w-5 text-teal-600" /> Analysis Defaults
          </CardTitle>
          <CardDescription>Default values for new analyses (can be overridden per analysis)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick Presets */}
          <div className="space-y-4 pb-6 border-b">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              <h4 className="font-semibold text-slate-800">Quick Presets</h4>
            </div>
            <p className="text-sm text-slate-500">Select a preset to quickly configure your organization's profile.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(PROFILE_PRESETS).map(([key, preset]) => (
                <button key={key} onClick={() => { setProfile(prev => ({ ...prev, ...preset.settings, dampening_factors: { ...prev.dampening_factors, ...preset.settings.dampening_factors } })); toast.success(`Applied "${preset.name}" preset`); }}
                  className={`p-3 rounded-lg border-2 text-left transition-all hover:shadow-md
                    ${preset.color === 'blue' ? 'border-blue-200 hover:border-blue-400 hover:bg-blue-50' : ''}
                    ${preset.color === 'teal' ? 'border-teal-200 hover:border-teal-400 hover:bg-teal-50' : ''}
                    ${preset.color === 'purple' ? 'border-purple-200 hover:border-purple-400 hover:bg-purple-50' : ''}
                    ${preset.color === 'amber' ? 'border-amber-200 hover:border-amber-400 hover:bg-amber-50' : ''}
                    ${preset.color === 'slate' ? 'border-slate-200 hover:border-slate-400 hover:bg-slate-50' : ''}
                    ${preset.color === 'orange' ? 'border-orange-200 hover:border-orange-400 hover:bg-orange-50' : ''}
                  `} data-testid={`preset-${key}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{preset.icon}</span>
                    <span className="font-medium text-slate-800 text-sm">{preset.name}</span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2">{preset.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Presets */}
          <div className="space-y-4 pb-6 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-teal-600" />
                <h4 className="font-semibold text-slate-800">Custom Presets</h4>
                {customPresets.length > 0 && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{customPresets.length}</span>}
              </div>
              <Button variant="outline" size="sm" onClick={() => setSavePresetDialogOpen(true)} className="text-teal-600 border-teal-200 hover:bg-teal-50" data-testid="save-preset-btn">
                <Plus className="h-4 w-4 mr-1" /> Save Current as Preset
              </Button>
            </div>
            {loadingPresets ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : customPresets.length === 0 ? (
              <div className="text-center py-6 bg-slate-50 rounded-lg"><p className="text-sm text-slate-500">No custom presets yet. Configure below and save as a preset.</p></div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {customPresets.map((preset) => (
                  <div key={preset.preset_id} className={`relative p-3 rounded-lg border-2 text-left transition-all hover:shadow-md group ${getPresetColorClass(preset.color)}`}>
                    <button onClick={() => applyCustomPreset(preset)} className="w-full text-left" data-testid={`custom-preset-${preset.preset_id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">{preset.icon}</span>
                        <span className="font-medium text-slate-800 text-sm">{preset.name}</span>
                      </div>
                      {preset.description && <p className="text-xs text-slate-500 line-clamp-2">{preset.description}</p>}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(preset.preset_id); }} className="absolute top-2 right-2 p-1 rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-50" data-testid={`delete-preset-${preset.preset_id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </button>
                    {deleteConfirmId === preset.preset_id && (
                      <div className="absolute inset-0 bg-white/95 rounded-lg flex flex-col items-center justify-center p-2">
                        <p className="text-xs text-slate-600 mb-2 text-center">Delete this preset?</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleDeletePreset(preset.preset_id)}>Delete</Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Elasticity Scenario */}
          <div>
            <Label className="text-sm font-medium text-slate-700">Preferred Elasticity Scenario</Label>
            <Select value={profile.preferred_elasticity_scenario} onValueChange={(v) => setProfile({ ...profile, preferred_elasticity_scenario: v })}>
              <SelectTrigger className="mt-1" data-testid="elasticity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="conservative">Conservative (Lower estimates)</SelectItem>
                <SelectItem value="moderate">Moderate (Balanced view)</SelectItem>
                <SelectItem value="aggressive">Aggressive (Higher estimates)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Maturity */}
          <div className="space-y-3 pt-4 border-t">
            <MaturityMethodToggle
              profile={profile}
              setProfile={setProfile}
              orgId={orgId}
            />
            {(profile.current_maturity || 50) > 75 && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">At high maturity levels, diminishing returns will apply to impact calculations.</div>
            )}
          </div>

          {/* Dampening Factors */}
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-slate-800">Dampening Factors</h4>
            <p className="text-sm text-slate-500">These factors affect how quickly and effectively changes take hold</p>
            <div className="space-y-4">
              {[
                { key: 'adoption_speed', label: 'Adoption Speed', desc: 'How quickly employees adopt new learning programs', default: 0.7 },
                { key: 'leadership_alignment', label: 'Leadership Alignment', desc: 'Level of leadership support for learning initiatives', default: 0.8 },
                { key: 'operational_readiness', label: 'Operational Readiness', desc: "Organization's infrastructure and process readiness", default: 0.75 },
              ].map(factor => (
                <div key={factor.key}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-700">{factor.label}</span>
                    <span className="text-teal-600 font-medium">{((profile.dampening_factors?.[factor.key] || factor.default) * 100).toFixed(0)}%</span>
                  </div>
                  <Slider value={[(profile.dampening_factors?.[factor.key] || factor.default) * 100]} onValueChange={([v]) => setProfile({ ...profile, dampening_factors: { ...profile.dampening_factors, [factor.key]: v / 100 } })} max={100} min={20} step={5} />
                  <p className="text-xs text-slate-500 mt-1">{factor.desc}</p>
                </div>
              ))}
            </div>
            <div className="p-3 bg-teal-50 rounded-lg mt-4">
              <p className="text-sm text-teal-800">
                <strong>Combined Dampening Factor:</strong>{' '}
                {((profile.dampening_factors?.adoption_speed || 0.7) * (profile.dampening_factors?.leadership_alignment || 0.8) * (profile.dampening_factors?.operational_readiness || 0.75) * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-teal-600 mt-1">Applied to all impact calculations to reflect realistic implementation conditions</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer Factor Methodology */}
      <TransferMethodologyCard orgId={orgId} />

      {/* Scale Dampening */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Users className="h-5 w-5 text-amber-600" /> Scale Dampening</CardTitle>
          <CardDescription>Adjust how organizational size affects projected impact</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={profile.scale_dampening_config?.mode || 'automatic'} onValueChange={(v) => setProfile({ ...profile, scale_dampening_config: { ...profile.scale_dampening_config, mode: v } })}>
            <SelectTrigger data-testid="scale-dampening-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="automatic">Automatic</SelectItem>
              <SelectItem value="custom">Custom Curve</SelectItem>
              <SelectItem value="fixed">Fixed Factor</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
          {profile.scale_dampening_config?.mode === 'custom' && (
            <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-700">Curve Adjustment</span>
                <span className="text-amber-600 font-medium">{((profile.scale_dampening_config?.custom_multiplier || 1.0) * 100).toFixed(0)}%</span>
              </div>
              <Slider value={[(profile.scale_dampening_config?.custom_multiplier || 1.0) * 100]} onValueChange={([v]) => setProfile({ ...profile, scale_dampening_config: { ...profile.scale_dampening_config, custom_multiplier: v / 100 } })} max={150} min={50} step={5} />
            </div>
          )}
          {profile.scale_dampening_config?.mode === 'fixed' && (
            <div className="space-y-2 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-700">Fixed Scale Factor</span>
                <span className="text-amber-600 font-medium">{((profile.scale_dampening_config?.fixed_factor || 1.0) * 100).toFixed(0)}%</span>
              </div>
              <Slider value={[(profile.scale_dampening_config?.fixed_factor || 1.0) * 100]} onValueChange={([v]) => setProfile({ ...profile, scale_dampening_config: { ...profile.scale_dampening_config, fixed_factor: v / 100 } })} max={100} min={10} step={5} />
            </div>
          )}
          {profile.scale_dampening_config?.mode === 'disabled' && (
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-rose-800">Scale Dampening Disabled</p>
                  <p className="text-xs text-rose-600 mt-1">Full theoretical impact will be used. Use with caution.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Learning Modality */}
      <Card className="border-purple-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><BookOpen className="h-5 w-5 text-purple-600" /> Learning Modality</CardTitle>
          <CardDescription>Primary delivery method affects knowledge transfer effectiveness</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={profile.primary_learning_modality || 'blended'} onValueChange={(v) => setProfile({ ...profile, primary_learning_modality: v })}>
            <SelectTrigger data-testid="learning-modality-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="on_the_job">On-the-Job Coaching (95%)</SelectItem>
              <SelectItem value="simulation">Simulation / VR Training (88%)</SelectItem>
              <SelectItem value="in_person_ilt">In-Person Instructor-Led (85%)</SelectItem>
              <SelectItem value="blended">Blended Learning (80%)</SelectItem>
              <SelectItem value="peer_learning">Peer Learning (78%)</SelectItem>
              <SelectItem value="virtual_ilt">Virtual Instructor-Led (75%)</SelectItem>
              <SelectItem value="microlearning">Microlearning (65%)</SelectItem>
              <SelectItem value="self_paced_elearning">Self-Paced E-Learning (55%)</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Time Lag & Decay */}
      <Card className="border-teal-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Clock className="h-5 w-5 text-teal-600" /> Time Lag & Decay</CardTitle>
          <CardDescription>Account for implementation lag, ramp-up period, and skill decay</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <Label className="text-slate-700">Implementation Lag</Label>
              <span className="text-teal-600 font-medium">{profile.time_lag_config?.implementation_lag_months || 2} months</span>
            </div>
            <Slider value={[profile.time_lag_config?.implementation_lag_months || 2]} onValueChange={([v]) => setProfile({ ...profile, time_lag_config: { ...profile.time_lag_config, implementation_lag_months: v } })} max={6} min={0} step={1} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <Label className="text-slate-700">Ramp-Up Period</Label>
              <span className="text-teal-600 font-medium">{profile.time_lag_config?.ramp_up_months || 3} months</span>
            </div>
            <Slider value={[profile.time_lag_config?.ramp_up_months || 3]} onValueChange={([v]) => setProfile({ ...profile, time_lag_config: { ...profile.time_lag_config, ramp_up_months: v } })} max={6} min={1} step={1} />
          </div>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-slate-700">Include Skill Decay</Label>
              <p className="text-xs text-slate-500">Account for knowledge loss over time</p>
            </div>
            <Checkbox checked={profile.time_lag_config?.include_decay || false} onCheckedChange={(c) => setProfile({ ...profile, time_lag_config: { ...profile.time_lag_config, include_decay: c } })} />
          </div>
          {profile.time_lag_config?.include_decay && (
            <div className="space-y-2 p-3 bg-rose-50 rounded-lg border border-rose-200">
              <div className="flex justify-between text-sm">
                <Label className="text-slate-700">Annual Decay Rate</Label>
                <span className="text-rose-600 font-medium">{((profile.time_lag_config?.annual_decay_rate || 0.15) * 100).toFixed(0)}%</span>
              </div>
              <Slider value={[(profile.time_lag_config?.annual_decay_rate || 0.15) * 100]} onValueChange={([v]) => setProfile({ ...profile, time_lag_config: { ...profile.time_lag_config, annual_decay_rate: v / 100 } })} max={30} min={5} step={5} />
            </div>
          )}
          <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs font-medium text-slate-700 mb-2">Impact Timeline</p>
            <div className="flex items-center gap-1 text-xs">
              <div className="flex-1 h-2 bg-slate-300 rounded" title="Implementation Lag" />
              <div className="flex-1 h-2 bg-teal-300 rounded" title="Ramp-Up" />
              <div className="flex-[2] h-2 bg-teal-500 rounded" title="Full Impact" />
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>Month 1</span>
              <span>Full impact: Month {(profile.time_lag_config?.implementation_lag_months || 2) + (profile.time_lag_config?.ramp_up_months || 3) + 1}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function TransferMethodologyCard({ orgId }) {
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = orgId ? `?organization_id=${orgId}` : '';
        const res = await api.get(`/modelling-settings/effective${params}`);
        setEffective(res.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetch();
  }, [orgId]);

  const handleToggle = async (useRoe) => {
    setSaving(true);
    try {
      await api.post('/modelling-settings/org', {
        organization_id: orgId,
        use_roe_composite: useRoe,
        roe_weights: effective?.settings?.roe_weights || { roe_l2: 0.25, roe_motivation: 0.35, roe_l3: 0.40 },
      });
      // Re-fetch
      const params = orgId ? `?organization_id=${orgId}` : '';
      const res = await api.get(`/modelling-settings/effective${params}`);
      setEffective(res.data);
      toast.success(useRoe ? 'Switched to ROE Composite (Measured)' : 'Switched to Synthetic (Estimated)');
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleWeightChange = async (key, value) => {
    const weights = { ...(effective?.settings?.roe_weights || {}), [key]: value };
    setSaving(true);
    try {
      await api.post('/modelling-settings/org', {
        organization_id: orgId,
        use_roe_composite: effective?.settings?.use_roe_composite || false,
        roe_weights: weights,
      });
      const params = orgId ? `?organization_id=${orgId}` : '';
      const res = await api.get(`/modelling-settings/effective${params}`);
      setEffective(res.data);
    } catch { toast.error('Failed to update weights'); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  const isRoe = effective?.effective_method === 'roe_composite';
  const hasAllRoe = effective?.roe_data?.all_available;
  const composite = effective?.roe_composite;
  const weights = effective?.settings?.roe_weights || {};

  return (
    <Card className="border-indigo-200" data-testid="transfer-methodology-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FlaskConical className="h-5 w-5 text-indigo-600" />
          Transfer Factor Methodology
          {isRoe ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Measured (ROE)</Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-200 text-[10px]">Estimated (Synthetic)</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Choose how transfer impact is calculated in new scenarios. Synthetic uses best-guess estimates; ROE Composite uses measured evaluation data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Toggle */}
        <div className="grid grid-cols-2 gap-3" data-testid="transfer-method-toggle">
          <button
            onClick={() => handleToggle(false)}
            disabled={saving}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              !isRoe ? 'border-amber-400 bg-amber-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="method-synthetic"
          >
            <div className="flex items-center gap-2 mb-1">
              <SlidersHorizontal className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-slate-800">Synthetic (Estimated)</span>
            </div>
            <p className="text-[11px] text-slate-500">Uses adoption speed, leadership alignment, and operational readiness as proxy estimates for transfer impact.</p>
          </button>
          <button
            onClick={() => hasAllRoe && handleToggle(true)}
            disabled={saving || !hasAllRoe}
            className={`p-4 rounded-lg border-2 text-left transition-all ${
              isRoe ? 'border-emerald-400 bg-emerald-50 shadow-sm' : hasAllRoe ? 'border-slate-200 hover:border-slate-300' : 'border-slate-200 opacity-50 cursor-not-allowed'
            }`}
            data-testid="method-roe-composite"
          >
            <div className="flex items-center gap-2 mb-1">
              <FlaskConical className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-800">ROE Composite (Measured)</span>
            </div>
            <p className="text-[11px] text-slate-500">Uses weighted average of L2 effectiveness, Motivation, and L3 behavior change from actual evaluation data.</p>
            {!hasAllRoe && (
              <p className="text-[10px] text-rose-500 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Requires all 3 ROE data types (L2 + Motivation + L3) to activate
              </p>
            )}
          </button>
        </div>

        {/* ROE Data Availability */}
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-500 font-medium">ROE Data:</span>
          {['roe_l2', 'roe_motivation', 'roe_l3'].map(key => {
            const available = effective?.roe_data?.[key];
            const labels = { roe_l2: 'L2', roe_motivation: 'Motivation', roe_l3: 'L3' };
            return (
              <span key={key} className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
                available ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-600'
              }`} data-testid={`roe-data-${key}`}>
                {available ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {labels[key]}
              </span>
            );
          })}
        </div>

        {/* ROE Composite Preview */}
        {composite && (
          <div className="bg-gradient-to-r from-indigo-50 to-white border border-indigo-200 rounded-lg p-4" data-testid="roe-composite-preview">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-indigo-800">ROE Composite Factor</span>
              <span className="text-xl font-bold text-indigo-700">{(composite.factor * 100).toFixed(1)}%</span>
            </div>
            <div className="space-y-2">
              {Object.entries(composite.components).map(([key, comp]) => {
                const labels = { roe_l2: 'L2 (Learning Effectiveness)', roe_motivation: 'Motivation', roe_l3: 'L3 (Behavior Transfer)' };
                return (
                  <div key={key} className="flex items-center gap-3 text-xs">
                    <span className="text-slate-600 w-36">{labels[key]}</span>
                    <span className="font-mono text-slate-800 w-12 text-right">{comp.score}</span>
                    <span className="text-slate-400">×</span>
                    <span className="font-mono text-slate-600 w-10">{(comp.weight * 100).toFixed(0)}%</span>
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-400 rounded-full" style={{ width: `${comp.contribution * 100 / composite.factor}%` }} />
                    </div>
                    <span className="font-mono text-indigo-700 w-12 text-right">{(comp.contribution * 100).toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Weight Overrides */}
        {effective?.settings?.use_roe_composite && (
          <div className="space-y-3 pt-3 border-t border-slate-200">
            <h4 className="text-sm font-medium text-slate-700">ROE Component Weights</h4>
            <p className="text-[11px] text-slate-500">Adjust the relative importance of each ROE evaluation level. Default weights align with Kirkpatrick/Phillips framework.</p>
            {[
              { key: 'roe_l2', label: 'L2 (Learning Effectiveness)', default: 0.25 },
              { key: 'roe_motivation', label: 'Motivation', default: 0.35 },
              { key: 'roe_l3', label: 'L3 (Behavior Transfer)', default: 0.40 },
            ].map(w => (
              <div key={w.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600">{w.label}</span>
                  <span className="text-indigo-600 font-medium">{((weights[w.key] || w.default) * 100).toFixed(0)}%</span>
                </div>
                <Slider
                  value={[(weights[w.key] || w.default) * 100]}
                  onValueChange={([v]) => handleWeightChange(w.key, v / 100)}
                  max={80} min={5} step={5}
                  data-testid={`weight-slider-${w.key}`}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
